import { useCallback, useEffect, useRef } from 'react';
import { engagementsApi, EngagementType } from 'src/api/engagements';

interface UseCommentViewingEngagementProps {
  postId: string;
  tribeId: string;
  hasComments: boolean;
  autoTrack?: boolean; // For desktop where comments show automatically
}

export const useCommentViewingEngagement = ({
  postId,
  tribeId,
  hasComments,
  autoTrack = true,
}: UseCommentViewingEngagementProps) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedRef = useRef<boolean>(false);

  const trackCommentViewing = useCallback(async () => {
    if (hasTrackedRef.current || !hasComments || !tribeId) {
      return;
    }

    try {
      await engagementsApi.upsertWatchedEngagements({
        type: EngagementType.COMMENTS_VIEWED,
        postId,
      });
      hasTrackedRef.current = true;
    } catch (error) {
      console.error('Error tracking comment viewing engagement:', error);
    }
  }, [tribeId, postId, hasComments]);

  const startTracking = useCallback(() => {
    if (hasTrackedRef.current || !hasComments || !tribeId) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Start 2-second timer
    timeoutRef.current = setTimeout(() => {
      trackCommentViewing();
    }, 2000);
  }, [trackCommentViewing, hasComments, tribeId]);

  const stopTracking = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const resetTracking = useCallback(() => {
    hasTrackedRef.current = false;
    stopTracking();
  }, [stopTracking]);

  // For manual tracking (when user clicks to view comments)
  const trackManually = useCallback(() => {
    if (!hasComments || !tribeId) {
      return;
    }
    startTracking();
  }, [startTracking, hasComments, tribeId]);

  // Auto-start tracking when component mounts and has comments (desktop)
  useEffect(() => {
    if (autoTrack && hasComments && tribeId) {
      startTracking();
    }

    return () => {
      stopTracking();
    };
  }, [hasComments, tribeId, autoTrack, startTracking, stopTracking]);

  // Reset tracking when postId changes (switching to different post)
  useEffect(() => {
    resetTracking();
  }, [postId, resetTracking]);

  return {
    startTracking,
    stopTracking,
    resetTracking,
    trackManually,
  };
};
